import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Loading from '@ingka/loading';
import Text from '@ingka/text';
import '@ingka/inline-message/dist/style.css';
import { useEffect, useState } from 'react';
import {
  type CatalogProductAdmin,
  createAdminProduct,
  getMe,
  listAdminProducts,
  updateAdminProduct,
} from '@/api/client';
import CatalogItemModal from '@/components/admin/CatalogItemModal';
import { useApp } from '@/context/AppContext';
import styles from './AdminCatalog.module.css';
import AdminEntityList from './AdminEntityList';

type ProductModalState =
  | { open: false }
  | { open: true; mode: 'create' }
  | { open: true; mode: 'edit'; product: CatalogProductAdmin };

export default function AdminProductsPage() {
  const [products, setProducts] = useState<CatalogProductAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [modal, setModal] = useState<ProductModalState>({ open: false });
  const { setUser } = useApp();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { products } = await listAdminProducts();
      setProducts(products);
      // Refresh the current user's catalog so wizard picks up changes
      const me = await getMe();
      setUser(me);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is unmemoized and intended to run once on mount; adding it would re-fetch on every render.
  useEffect(() => {
    load();
  }, []);

  const handleSave = async (data: {
    id: string;
    name: string;
    description: string;
    active?: boolean;
  }) => {
    if (modal.open && modal.mode === 'create') {
      await createAdminProduct({
        id: data.id,
        name: data.name,
        description: data.description || null,
      });
    } else if (modal.open && modal.mode === 'edit') {
      await updateAdminProduct(modal.product.id, {
        name: data.name,
        description: data.description || null,
        active: data.active,
      });
    }
    setModal({ open: false });
    await load();
  };

  return (
    <>
      <div className={styles.header}>
        <div>
          <Text tagName="h1" textStyle="Heading.L">
            Products
          </Text>
          <Text tagName="p" textStyle="Body.M" className={styles.subtitle}>
            Manage products and their entities. Changes take effect on the next user sign-in.
          </Text>
        </div>
        <Button
          type="emphasised"
          text="Add product"
          onClick={() => setModal({ open: true, mode: 'create' })}
        />
      </div>

      {error && (
        <div className={styles.errorWrap}>
          <InlineMessage variant="negative" title="Error" body={error} />
        </div>
      )}

      {loading ? (
        <div className={styles.loadingWrap}>
          <Loading text="Loading products..." />
        </div>
      ) : (
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th className={styles.th}></th>
                <th className={styles.th}>Name</th>
                <th className={styles.th}>ID</th>
                <th className={styles.th}>Description</th>
                <th className={styles.th}>Route</th>
                <th className={styles.th}>Entities</th>
                <th className={styles.th}>Active</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const isExpanded = expandedId === p.id;
                return (
                  <>
                    <tr key={p.id} className={styles.tr}>
                      <td className={styles.td}>
                        <button
                          type="button"
                          className={styles.expandBtn}
                          onClick={() => setExpandedId(isExpanded ? null : p.id)}
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      </td>
                      <td className={styles.td}>{p.name}</td>
                      <td className={styles.td}>
                        <code className={styles.idCell}>{p.id}</code>
                      </td>
                      <td className={styles.td}>{p.description || '—'}</td>
                      <td className={styles.td}>
                        {p.special_route ? (
                          <code className={styles.idCell}>{p.special_route}</code>
                        ) : (
                          <span className={styles.dimText}>wizard</span>
                        )}
                      </td>
                      <td className={styles.td}>
                        {p.entities.filter((e) => e.active).length} active
                        {p.entities.filter((e) => !e.active).length > 0 && (
                          <span className={styles.dimText}>
                            {' '}
                            ({p.entities.filter((e) => !e.active).length} inactive)
                          </span>
                        )}
                      </td>
                      <td className={styles.td}>
                        {p.active ? (
                          <span className={styles.activeOn}>●</span>
                        ) : (
                          <span className={styles.activeOff}>●</span>
                        )}
                      </td>
                      <td className={styles.tdRight}>
                        <Button
                          type="secondary"
                          size="small"
                          text="Edit"
                          onClick={() => setModal({ open: true, mode: 'edit', product: p })}
                        />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className={styles.expandedRow}>
                          <AdminEntityList product={p} onReload={load} />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal.open && (
        <CatalogItemModal
          title={modal.mode === 'create' ? 'Add product' : `Edit product`}
          mode={modal.mode}
          initial={
            modal.mode === 'edit'
              ? {
                  id: modal.product.id,
                  name: modal.product.name,
                  description: modal.product.description ?? '',
                  active: modal.product.active,
                }
              : undefined
          }
          warningBody="Coordinate with the data team before creating. If this product needs a dedicated UI (like Locations), also coordinate with engineering — they must build the page and set the special_route column."
          onClose={() => setModal({ open: false })}
          onSave={handleSave}
        />
      )}
    </>
  );
}
